import { useEffect, useRef } from 'react';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import { CircularProgress, Stack, Typography } from '@mui/material';

import type { RocDocumentDownloadArgs } from '@/components/roc/DocumentMessageCard';
import { ReferralMessage } from '@/components/roc/ReferralMessage';
import type { ReferralSide } from '@/pages/CRM/ReferralOutbound/lib/callerIdentity';
import { type ReadState } from '@/pages/CRM/ReferralOutbound/ReferralManagement/lib/readReceipts';
import { type ReferralMessageModel } from '@/pages/CRM/ReferralOutbound/ReferralManagement/models/ReferralMessageModel';

interface ConversationMessagesPanelProps {
  messages: ReferralMessageModel[];
  resetKey: string;
  /** Which side of the referral is looking — see ReferralMessage. */
  viewerSide: ReferralSide;
  referralSenderFacilityId: string;
  ownSideName: string;
  getFacilityName: (facilityId: string) => string;
  readStateByMessageId?: Record<string, ReadState | null | undefined>;
  onOpenDocument?: (args: RocDocumentDownloadArgs) => Promise<void>;
  isLoading?: boolean;
  isError?: boolean;
  emptyMessage?: string;
  errorMessage?: string;
}

export default function ConversationMessagesPanel({
  messages,
  resetKey,
  viewerSide,
  referralSenderFacilityId,
  ownSideName,
  getFacilityName,
  readStateByMessageId,
  onOpenDocument,
  isLoading = false,
  isError = false,
  emptyMessage = 'No messages yet. Send the first message below.',
  errorMessage = 'Failed to load messages. Please try again.'
}: ConversationMessagesPanelProps) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const lastMessageIdRef = useRef<string | null>(null);
  const hasScrolledRef = useRef(false);
  const resetKeyRef = useRef<string | null>(null);

  if (resetKeyRef.current !== resetKey) {
    resetKeyRef.current = resetKey;
    hasScrolledRef.current = false;
    lastMessageIdRef.current = null;
  }

  useEffect(() => {
    if (messages.length === 0) {
      return;
    }

    const lastMessage = messages[messages.length - 1];
    if (!lastMessage) {
      return;
    }

    const isNewMessage = lastMessage.message_id !== lastMessageIdRef.current;
    const behavior: ScrollBehavior =
      isNewMessage && hasScrolledRef.current ? 'smooth' : 'auto';
    lastMessageIdRef.current = lastMessage.message_id;
    hasScrolledRef.current = true;
    bottomRef.current?.scrollIntoView({ behavior });
  }, [messages]);

  if (isLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" flex={1} py={6}>
        <CircularProgress size={24} />
      </Stack>
    );
  }

  if (isError) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        flex={1}
        py={6}
        gap={1}
      >
        <ForumOutlinedIcon sx={{ fontSize: 36, color: 'text.disabled' }} />
        <Typography variant="body2" color="text.secondary" textAlign="center">
          {errorMessage}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack
      sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 3, py: 2 }}
      gap={2}
    >
      {messages.length === 0 && (
        <Stack alignItems="center" justifyContent="center" flex={1} py={6}>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            {emptyMessage}
          </Typography>
        </Stack>
      )}

      {messages.map((message, index) => (
        <ReferralMessage
          key={message.message_id}
          model={message}
          viewerSide={viewerSide}
          referralSenderFacilityId={referralSenderFacilityId}
          ownSideName={ownSideName}
          prevModel={index > 0 ? (messages[index - 1] ?? null) : null}
          otherSideName={getFacilityName(message.sender_facility_id)}
          readState={readStateByMessageId?.[message.message_id] ?? null}
          onOpenDocument={onOpenDocument}
        />
      ))}

      <div ref={bottomRef} />
    </Stack>
  );
}
